import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ProjectModal from '../components/ProjectModal';
import ServiceModal from '../components/ServiceModal';
import ProcessModal from '../components/ProcessModal';
import LazyVideo from '../components/LazyVideo';
import { projects } from '../data/projects';
import { services } from '../data/services';
import { processSteps } from '../data/processSteps';
import { getProcessIcon } from '../components/ProcessIcons';
import { ChevronLeft, ChevronRight, ArrowRight, PhoneIcon, MailIcon, MapPinIcon } from '../components/Icons';
import useSwipe from '../hooks/useSwipe';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG, DEFAULT_SEO, schemas } from '../config/seo';
import ResponsiveImage from '../components/ResponsiveImage';
import ContactForm from '../components/ContactForm';

const HomePage = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedProcess, setSelectedProcess] = useState(null);
    const location = useLocation();

    // Handle hash navigation from other pages
    useEffect(() => {
        if (location.hash) {
            const sectionId = location.hash.replace('#', '');
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const navHeight = 80;
                    const targetPosition = element.offsetTop - navHeight;
                    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                }
            }, 100);
        }
    }, [location]);

    // Carousel navigation
    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % projects.length);
    }, []);
    
    const prevSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev - 1 + projects.length) % projects.length);
    }, []);

    const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(
        nextSlide,  // swipe left = next slide
        prevSlide   // swipe right = previous slide
    );

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    // Auto-rotation - 7 seconds
    useEffect(() => {
        if (isPaused) return;
        const interval = setInterval(nextSlide, 7000);
        return () => clearInterval(interval);
    }, [isPaused, nextSlide, currentSlide]);

    // Smooth scroll helper
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navHeight = 80;
            const targetPosition = element.offsetTop - navHeight;
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
    };

    return (
        <>
            <Helmet>
                <title>{DEFAULT_SEO.title}</title>
                <meta name="description" content={DEFAULT_SEO.description} />
                <link rel="canonical" href={SITE_CONFIG.url} />

                <meta property="og:title" content={DEFAULT_SEO.title} />
                <meta property="og:description" content={DEFAULT_SEO.description} />
                <meta property="og:image" content={`${SITE_CONFIG.url}${SITE_CONFIG.images.og}`} />
                <meta property="og:url" content={SITE_CONFIG.url} />
                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={DEFAULT_SEO.title} />
                <meta name="twitter:description" content={DEFAULT_SEO.description} />
                <meta name="twitter:image" content={`${SITE_CONFIG.url}${SITE_CONFIG.images.og}`} />

                <script type="application/ld+json">
                    {JSON.stringify(schemas.organization())}
                </script>
            </Helmet>

            {/* Hero Section */}
            <section className="hero-section section-padding" id="home">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 fade-in-up">
                            <span className="hero-label">Historic Glass & Door Restoration</span>
                            <h1 className="hero-title">
                                Preserving<br />
                                History<br />
                                Since 2009
                            </h1>
                            <div className="divider"></div>
                            <p className="hero-text">
                                Vintage Glass Works is a trusted partner in historic glass and architectural restoration, bringing deep experience and expert craftsmanship to every project. From sacred stained glass to distinctive leaded windows in landmark buildings, we work alongside property owners, developers, and contractors to preserve architectural legacy—one window, one door, one building at a time.
                            </p>
                            <div className="d-flex flex-wrap gap-3">
                                <button className="btn-primary-custom" onClick={() => scrollToSection('portfolio')}>
                                    View Our Work
                                </button>
                                <button className="btn-secondary-custom" onClick={() => scrollToSection('contact')}>
                                    Request Consultation
                                </button>
                            </div>
                        </div>
                        <div className="col-lg-6 fade-in-up stagger-2">
                            <div className="hero-image-wrapper">
                                <div className="hero-image-frame"></div>
                                <ResponsiveImage
                                    src="/images/vgw-hero.png"
                                    alt="Historic stained glass restoration"
                                    className="hero-image"
                                    loading="eager"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="quote-section section-padding">
                <div className="quote-mark">"</div>
                <div className="container position-relative" style={{ zIndex: 1 }}>
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center">
                            <blockquote className="quote-text">
                                "If eyes are the windows to our souls, then windows are the eyes into the soul of a house."
                            </blockquote>
                            <cite className="quote-author">— Rose Tarlow</cite>
                        </div>
                    </div>
                </div>
            </section>

            {/* Carousel Section */}
            <section className="carousel-section section-padding" id="portfolio">
                <div className="container">
                    <div className="row align-items-end mb-4">
                        <div className="col-md-8">
                            <span className="section-label">Selected Work</span>
                            <h2 className="section-title">Recent Projects</h2>
                        </div>
                        <div className="col-md-4">
                            <div className="carousel-controls justify-content-md-end">
                                <button className="carousel-btn" onClick={prevSlide} aria-label="Previous slide">
                                    <ChevronLeft />
                                </button>
                                <button className="carousel-btn active" onClick={nextSlide} aria-label="Next slide">
                                    <ChevronRight />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        className="carousel-container"
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <div
                            className="carousel-track"
                            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                        >
                            {projects.map((project, index) => (
                                <div className="carousel-slide" key={project.id}>
                                    <div className="row align-items-center">
                                        <div className="col-lg-6">
                                            <div className="carousel-image-container">
                                                <ResponsiveImage src={project.images[0]}
                                                    alt={project.title}
                                                    className="carousel-image"
                                                    loading={index === 0 ? "eager" : "lazy"} />
                                                <div className="carousel-category">
                                                    <span>{project.category}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="carousel-content">
                                                <span className="project-number">0{index + 1}</span>
                                                <h3 className="project-title">{project.title}</h3>
                                                <div className="project-meta">
                                                    <span className="project-location">{project.location}</span>
                                                    <span className="project-year">{project.year}</span>
                                                </div>
                                                <div className="divider"></div>
                                                <p className="project-description">{project.description}</p>
                                                <button
                                                    className="project-link"
                                                    onClick={() => setSelectedProject(project)}
                                                >
                                                    View Project
                                                    <span className="project-link-icon">
                                                        <ArrowRight />
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="carousel-indicators-custom">
                        {projects.map((_, index) => (
                            <button
                                key={index}
                                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Laser Restoration Feature Section */}
            <section className="laser-feature-section section-padding" id="laser-restoration">
                <LazyVideo
                    webmSrc="/videos/services/laser/laser-restoration.webm"
                    mp4Src="/videos/services/laser/laser-restoration.mp4"
                    poster="/images/services/laser/laser-restoration.jpg"
                />

                <div className="container">
                    <div className="laser-content">
                        <span className="laser-badge">New Service</span>
                        <h2 className="laser-title">Laser Restoration</h2>
                        <p className="laser-subtitle">Precision Restoration for Historic Surfaces</p>
                        <p className="laser-description">
                            State-of-the-art laser technology that removes decades of grime, paint, and corrosion
                            without touching the original surface. See the difference precision makes.
                        </p>
                        <div className="laser-features">
                            <div className="laser-feature">
                                <span className="laser-feature-icon">✦</span>
                                <span>Non-Abrasive</span>
                            </div>
                            <div className="laser-feature">
                                <span className="laser-feature-icon">✦</span>
                                <span>Chemical-Free</span>
                            </div>
                            <div className="laser-feature">
                                <span className="laser-feature-icon">✦</span>
                                <span>Precision Control</span>
                            </div>
                        </div>
                        <div className="laser-cta">
                            <button
                                className="btn-primary-custom"
                                onClick={() => {
                                    const service = services.find(s => s.title === "Laser Restoration");
                                    if (service) setSelectedService(service);
                                }}
                            >
                                Learn More
                            </button>
                            <button
                                className="btn-secondary-custom-light"
                                onClick={() => {
                                    const element = document.getElementById('contact');
                                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                Request Consultation
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="services-section section-padding" id="services">
                <div className="container">
                    <div className="row justify-content-center text-center mb-5">
                        <div className="col-lg-8">
                            <span className="section-label">What We Do</span>
                            <h2 className="section-title">Our Services</h2>
                            <div className="divider mx-auto"></div>
                            <p className="services-intro">
                                Expert restoration and fabrication for historic buildings throughout New England. We partner with developers, architects, general contractors, and educational and religious institutions to preserve historic and nostalgic architectural features.
                            </p>
                        </div>
                    </div>

                    <div className="services-grid">
                        {services.map((service, index) => (
                            <div
                                className={`service-card hover-card-sm ${service.featured ? 'service-card-featured' : ''}`}
                                key={index}
                                onClick={() => setSelectedService(service)}
                            >
                                <div className="service-card-image-wrapper img-hover-zoom">
                                    <ResponsiveImage
                                        src={service.images[0]}
                                        alt={service.title}
                                        className="service-card-image"
                                        loading={index === 0 ? "eager" : "lazy"}
                                    />
                                    <span className={`service-badge service-badge-${service.badgeType}`}>
                                        {service.badge}
                                    </span>
                                </div>

                                {service.featured ? (
                                    <div className="service-card-body">
                                        <div className="service-card-content-left">
                                            <span className="service-card-number">{service.number}</span>
                                            <h3 className="service-card-title">{service.title}</h3>
                                            <p className="service-card-description">{service.description}</p>
                                        </div>
                                        <div className="service-card-content-right">
                                            <ul className="service-card-features">
                                                {service.features.map((feature, i) => (
                                                    <li key={i}>{feature}</li>
                                                ))}
                                            </ul>
                                            <div className="service-card-link-wrapper">
                                                <span className="service-card-link">
                                                    Learn More
                                                    <span className="service-card-link-icon">
                                                        <ArrowRight />
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="service-card-body">
                                        <span className="service-card-number">{service.number}</span>
                                        <h3 className="service-card-title">{service.title}</h3>
                                        <p className="service-card-description">{service.description}</p>
                                        <ul className="service-card-features">
                                            {service.features.map((feature, i) => (
                                                <li key={i}>{feature}</li>
                                            ))}
                                        </ul>
                                        <div className="service-card-link-wrapper">
                                            <span className="service-card-link">
                                                Learn More
                                                <span className="service-card-link-icon">
                                                    <ArrowRight />
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="about-section section-padding" id="about">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="about-image-wrapper">
                                <div className="about-image-bg"></div>
                                <img
                                    src="/images/about-us-hero.png"
                                    alt="Craftsman at work"
                                    className="about-image"
                                />
                                <div className="about-badge">
                                    <span className="about-badge-icon">✦</span>
                                    <span className="about-badge-text">Trusted Preservation Experts</span>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <span className="section-label">Our Story</span>
                            <h2 className="about-title">
                                Over 15 Years of<br />
                                Preservation Excellence
                            </h2>
                            <div className="divider"></div>
                            <p className="about-text">
                                Vintage Glass Works began as a window and door restoration company dedicated to historic properties. As preservation needs evolved and demand grew, our expertise expanded to include stained and leaded glass restoration, custom glass creation, and the careful repair of historic architectural features.
                            </p>
                            <p className="about-text">
                                We bring unparalleled craftsmanship, meticulous attention to detail, and a deep understanding of how time affects the buildings where we live, work, learn, and worship. We know which techniques, materials, and methods are needed to restore these treasured features while meeting modern performance standards.
                            </p>
                            <p className="about-text">
                                Our focus is commercial and institutional work—churches, campuses, municipal buildings, and historic properties of significance. We serve as a trusted partner to property owners, developers, and general contractors, providing the specialized expertise that complex restoration projects demand.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="process-section section-padding" id="process">
                <div className="container">
                    <div className="row justify-content-center text-center mb-5">
                        <div className="col-lg-8">
                            <span className="section-label">How We Work</span>
                            <h2 className="section-title">Our Process</h2>
                            <div className="divider mx-auto"></div>
                            <p className="process-intro">
                                Every historic building tells a story. Our process is designed to understand that story,
                                document what remains, and craft a preservation plan that honors the past while meeting
                                the demands of modern use.
                            </p>
                        </div>
                    </div>

                    <div className="process-grid">
                        <div className="process-flow-line"></div>

                        {processSteps.map((step, index) => (
                            <div
                                className="process-card"
                                key={index}
                                onClick={() => setSelectedProcess(step)}
                            >
                                <div className="process-card-number">{step.number}</div>
                                <div className="process-card-icon">
                                    {getProcessIcon(step.iconName)}
                                </div>
                                <h3 className="process-card-title">{step.title}</h3>
                                <span className="process-card-tagline">{step.tagline}</span>
                                <p className="process-card-text">{step.description}</p>
                                <span className="process-card-link">Learn More →</span>
                            </div>
                        ))}
                    </div>

                    <div className="process-cta">
                        <p className="process-cta-text">Ready to begin? Let's start with a conversation about your building.</p>
                        <button
                            className="btn-primary-custom"
                            onClick={() => {
                                const element = document.getElementById('contact');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                        >
                            Schedule a Consultation
                        </button>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="contact-section section-padding" id="contact">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-5">
                            <span className="section-label">Get In Touch</span>
                            <h2 className="contact-title">
                                Every Project Starts with a Conversation
                            </h2>
                            <div className="divider"></div>
                            <p className="contact-text">
                                Have a historic glass or door restoration project? We'd love to hear about it—whether it's a single window or an entire campus.
                            </p>

                            <div className="contact-info">
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <PhoneIcon />
                                    </div>
                                    <div>
                                        <span className="contact-item-label">Call Us</span>
                                        <span className="contact-item-value">(207) 588-7000</span>
                                    </div>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <MailIcon />
                                    </div>
                                    <div>
                                        <span className="contact-item-label">Email</span>
                                        <span className="contact-item-value">info@vintageglassworks.com</span>
                                    </div>
                                </div>
                                <div className="contact-item">
                                    <div className="contact-icon">
                                        <MapPinIcon />
                                    </div>
                                    <div>
                                        <span className="contact-item-label">Location</span>
                                        <span className="contact-item-value">Gardiner, Maine</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6 offset-lg-1">
                            <ContactForm />
                        </div>
                    </div>
                </div>
            </section>

            {/* Project Modal */}
            {selectedProject && (
                <ProjectModal
                    project={selectedProject}
                    onClose={() => setSelectedProject(null)}
                />
            )}

            {/* Service Modal */}
            {selectedService && (
                <ServiceModal
                    service={selectedService}
                    onClose={() => setSelectedService(null)}
                />
            )}

            {/* Process Modal */}
            {selectedProcess && (
                <ProcessModal
                    process={selectedProcess}
                    onClose={() => setSelectedProcess(null)}
                />
            )}
        </>
    );
};

export default HomePage;